import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Fade,
  Slide,
  Switch,
  Autocomplete,
} from "@mui/material";
import imageCompression from "browser-image-compression";
import { API_BASE_URL, getAuthHeaders, getUploadAuthHeaders } from "../API/Api";
import { filterBrandOptions, useBrandSearch } from "../Brands/brandUtils";
import {
  buildProductPayload,
  fetchCategories,
  fetchProductById,
  getProductApiErrorMessage,
} from "./productUtils";
const getTextValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.en || value.ku || value.ar || "";
  return String(value);
};
const flattenCategoryTree = (nodes = [], depth = 0) => {
  const list = [];
  nodes.forEach((node) => {
    const currentName = getTextValue(node?.name);

    const children = Array.isArray(node?.children)
      ? node.children
      : Array.isArray(node?.childrens)
        ? node.childrens
        : [];
    const isParent = children.length > 0;
    list.push({
      id: node.id,
      label: currentName,
      depth,
      selectable: !isParent,
      filters: Array.isArray(node?.filters) ? node.filters : [],
    });

    if (children.length > 0) {
      list.push(...flattenCategoryTree(children, depth + 1));
    }
  });
  return list;
};

const UpdateProduct = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    sku: "",
    code: "",
    ingredients: "",
    price: "",
    case_size: "",
    layer: "",
    pallet: "",
    unit_barcode: "",
    case_barcode: "",
    net_weight: "",
    vat: "",
    case_length: "",
    case_width: "",
    case_height: "",
    gross_weight: "",
    units_per_case: "",
    tariff: "",
    origin: "",
    image: "",
    brand_id: "",
    category_id: "",
    filter_ids: [],
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const { brands, loadingBrands } = useBrandSearch(selectedBrand);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  // Tracks whether the product has been loaded so the filter useEffect
  // doesn't prune filter_ids before product data arrives.
  const productLoadedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const getCategories = async () => {
      try {
        const categoriesData = await fetchCategories(controller.signal);
        if (cancelled) return;
        setCategories(flattenCategoryTree(categoriesData));
      } catch (error) {
        if (error?.name === "AbortError" || cancelled) return;
        console.error("Error fetching categories:", error);
        message.error("Failed to load categories");
      }
    };

    const fetchProduct = async () => {
      try {
        const product = await fetchProductById(id, controller.signal);
        if (cancelled) return;

        if (!product) {
          message.error("Product not found");
          return;
        }

        setFormData({
          name: product.name || "",
          slug: product.slug || "",
          sku: product.sku || "",
          code: product.code || "",
          ingredients: product.ingredients || "",
          price: product.price || "",
          case_size: product.case_size || "",
          layer: product.layer ?? "",
          pallet: product.pallet ?? "",
          unit_barcode: product.unit_barcode || "",
          case_barcode: product.case_barcode || "",
          net_weight: product.net_weight ?? "",
          vat: product.vat ?? "",
          case_length: product.case_length ?? "",
          case_width: product.case_width ?? "",
          case_height: product.case_height ?? "",
          gross_weight: product.gross_weight ?? "",
          units_per_case: product.units_per_case ?? product.units ?? "",
          tariff: product.tariff ?? "",
          origin: product.origin || "",
          image: product.image || "",
          brand_id: product?.brand?.id || "",
          category_id: product?.category?.id || "",
          filter_ids: Array.isArray(product?.filters)
            ? product.filters.map((item) => item.id)
            : [],
          is_active: !!product.is_active,
        });
        setPreviewUrl(
          product.image_url ||
            product?.images?.[0]?.image_url ||
            product?.images?.[0]?.url ||
            "",
        );

        setSelectedBrand(product.brand?.id ? product.brand : null);
      } catch (error) {
        if (error?.name === "AbortError" || cancelled) return;
        console.error("Error loading product:", error);
        message.error("Failed to load product");
      } finally {
        if (!cancelled) {
          productLoadedRef.current = true;
          setLoading(false);
        }
      }
    };

    fetchProduct();
    getCategories();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    if (!formData.category_id || categories.length === 0) return;
    const selectedCategory = categories.find(
      (item) => String(item.id) === String(formData.category_id),
    );
    const categoryFilters = selectedCategory?.filters || [];
    setFilters(categoryFilters);

    // Only prune filter_ids after the product has been loaded.
    // Before that, categories may arrive first and would wrongly clear
    // the product's pre-selected filters.
    if (productLoadedRef.current) {
      setFormData((prev) => ({
        ...prev,
        filter_ids: (prev.filter_ids || []).filter((selectedId) =>
          categoryFilters.some(
            (filter) => String(filter.id) === String(selectedId),
          ),
        ),
      }));
    }
  }, [categories, formData.category_id]);

  const handleCategoryChange = (selectedCategory) => {
    const categoryId = selectedCategory?.id ?? "";
    const categoryFilters = selectedCategory?.filters || [];
    setFilters(categoryFilters);
    setFormData((prev) => ({
      ...prev,
      category_id: categoryId,
      filter_ids: [],
    }));
  };

  const handleImageUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploading(true);
    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
        initialQuality: 0.85,
        alwaysKeepResolution: true,
      });
      const uploadFormData = new FormData();
      uploadFormData.append("image", compressedFile);
      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        headers: getUploadAuthHeaders(),
        body: uploadFormData,
      });
      if (!uploadRes.ok)
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      const uploadData = await uploadRes.json();
      const uploadedPath =
        uploadData?.image_path ||
        uploadData?.path ||
        uploadData?.data?.path ||
        uploadData?.url ||
        "";
      if (!uploadedPath) throw new Error("Upload response missing file path");
      setFormData((prev) => ({ ...prev, image: uploadedPath }));
      if (uploadData?.url) setPreviewUrl(uploadData.url);
      // message.success("Image uploaded");
    } catch (error) {
      console.error("Image compression/upload failed:", error);
      // message.error("Failed to upload image");
    } finally {
      URL.revokeObjectURL(localPreview);
      setUploading(false);
    }
  };

  const onChangeUpload = (e) => {
    if (e?.target?.files?.[0]) handleImageUpload(e.target.files[0]);
  };

  const handleChange = (key) => (e) => {
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      message.warning("Please enter product name");
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildProductPayload(formData);

      const res = await fetch(`${API_BASE_URL}/admin/products/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          getProductApiErrorMessage(
            errorData,
            `HTTP error! Status: ${res.status}`,
          ),
        );
      }

      message.success("Product updated successfully");
      window.location.href = "/admin/products";
    } catch (error) {
      console.error("Error updating product:", error);
      message.error(error.message || "Failed to update product");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategoryOption =
    formData.category_id === "" || formData.category_id === null
      ? null
      : categories.find((c) => String(c.id) === String(formData.category_id)) ||
        null;

  const selectedFilterOptions = (formData.filter_ids || [])
    .map((id) => filters.find((f) => String(f.id) === String(id)) || null)
    .filter(Boolean);

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography>Loading product data...</Typography>
      </Box>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1000, mx: "auto", my: 5, px: 2, marginBottom: 15 }}>
        <Slide direction="up" in timeout={400}>
          <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Box component="form" onSubmit={onSubmit}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Update Product
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  backgroundColor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    mx: "auto",
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "common.white",
                  }}
                >
                  {previewUrl ? (
                    <Box
                      component="img"
                      src={previewUrl}
                      alt="Product preview"
                      referrerPolicy="no-referrer"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image
                    </Typography>
                  )}
                </Box>
                <input
                  id="product-image-upload-update"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={onChangeUpload}
                />
                <label htmlFor="product-image-upload-update">
                  <Button variant="outlined" component="span">
                    {uploading ? "Uploading..." : "Upload Product Image"}
                  </Button>
                </label>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={formData.name}
                    onChange={handleChange("name")}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SKU"
                    value={formData.sku}
                    onChange={handleChange("sku")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={formData.code}
                    onChange={handleChange("code")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Ingredients"
                    value={formData.ingredients}
                    onChange={handleChange("ingredients")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Price"
                    value={formData.price}
                    onChange={handleChange("price")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Case Size"
                    value={formData.case_size}
                    onChange={handleChange("case_size")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Layer"
                    value={formData.layer}
                    onChange={handleChange("layer")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pallet"
                    value={formData.pallet}
                    onChange={handleChange("pallet")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Unit Barcode"
                    value={formData.unit_barcode}
                    onChange={handleChange("unit_barcode")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Case Barcode"
                    value={formData.case_barcode}
                    onChange={handleChange("case_barcode")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Net Weight"
                    value={formData.net_weight}
                    onChange={handleChange("net_weight")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Gross Weight"
                    value={formData.gross_weight}
                    onChange={handleChange("gross_weight")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="VAT"
                    value={formData.vat}
                    onChange={handleChange("vat")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Case Length"
                    value={formData.case_length}
                    onChange={handleChange("case_length")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Case Width"
                    value={formData.case_width}
                    onChange={handleChange("case_width")}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Case Height"
                    value={formData.case_height}
                    onChange={handleChange("case_height")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Units Per Case"
                    value={formData.units_per_case}
                    onChange={handleChange("units_per_case")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tariff"
                    value={formData.tariff}
                    onChange={handleChange("tariff")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Origin"
                    value={formData.origin}
                    onChange={handleChange("origin")}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={brands}
                    value={selectedBrand}
                    loading={loadingBrands}
                    onChange={(event, newValue) => {
                      setSelectedBrand(newValue);
                      setFormData((prev) => ({
                        ...prev,
                        brand_id: newValue?.id ?? "",
                      }));
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    filterOptions={filterBrandOptions}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    noOptionsText={
                      loadingBrands ? "Loading brands..." : "No brands found"
                    }
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Brand"
                        placeholder="Search brands..."
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={categories}
                    value={selectedCategoryOption}
                    onChange={(event, newValue) =>
                      handleCategoryChange(newValue)
                    }
                    getOptionLabel={(option) => option?.label || ""}
                    getOptionDisabled={(option) => !option.selectable}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    renderOption={(props, option) => (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          pl: option.depth * 2,
                          fontWeight: option.selectable ? 400 : 700,
                          opacity: option.selectable ? 1 : 0.65,
                        }}
                      >
                        {option.label}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    multiple
                    options={filters}
                    value={selectedFilterOptions}
                    onChange={(event, newValues) => {
                      setFormData((prev) => ({
                        ...prev,
                        filter_ids: newValues.map((v) => v.id),
                      }));
                    }}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    disableCloseOnSelect
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        {option.name}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Filters" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Active
                  </Typography>
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => (window.location.href = "/admin/products")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
};

export default UpdateProduct;
